
import { Routes, Route, Navigate } from "react-router-dom";
import Index from "@/pages/Index";
import LandingPage from "@/pages/LandingPage";
import GroupPage from "@/pages/GroupPage";
import AuthPage from "@/pages/AuthPage";
import NotFound from "@/pages/NotFound";
import GroupInvitePage from "@/pages/GroupInvitePage";
import PaymentMethodsPage from "@/pages/PaymentMethodsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import BlogAdminPage from "@/pages/BlogAdminPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import { isUserAdmin } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface AppRoutesProps {
  user: any | null;
}

const AdminRoute = ({ children, user }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      try {
        const adminStatus = await isUserAdmin(user.id);
        console.log('Admin route check:', { adminStatus, userId: user.id, userName: user.user_metadata?.username });
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error checking admin status in route:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAdmin();
  }, [user]);

  if (loading) {
    return null; // Or a loading spinner
  }

  if (!isAdmin) {
    return <Navigate to="/app" replace />;
  }

  return children;
};

const AppRoutes = ({ user }: AppRoutesProps) => {
  // For all routes, handle authentication
  return (
    <Routes>
      {/* Landing page route */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth routes */}
      <Route path="/login" element={user ? <Navigate to="/app" /> : <AuthPage />} />
      <Route path="/signup" element={user ? <Navigate to="/app" /> : <AuthPage initialMode="signup" />} />

      {/* App routes - protected routes that require authentication */}
      <Route path="/app" element={user ? <Index /> : <Navigate to="/login" />} />
      <Route path="/group/:groupId" element={user ? <GroupPage /> : <Navigate to="/login" />} />
      <Route path="/settings/payment-methods" element={user ? <PaymentMethodsPage /> : <Navigate to="/login" />} />
      
      {/* Admin routes */}
      <Route 
        path="/admin/dashboard" 
        element={
          user ? (
            <AdminRoute user={user}>
              <AdminDashboardPage />
            </AdminRoute>
          ) : (
            <Navigate to="/login" />
          )
        } 
      />
      
      {/* Blog routes - public routes */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:postId" element={<BlogPostPage />} />
      
      {/* Blog admin routes - protected routes that require authentication */}
      <Route 
        path="/blog/admin" 
        element={
          user ? (
            <AdminRoute user={user}>
              <BlogAdminPage />
            </AdminRoute>
          ) : (
            <Navigate to="/login" />
          )
        } 
      />
      
      {/* Invitation routes - accessible to everyone */}
      <Route path="/invite/:groupId" element={<GroupInvitePage />} />
      
      {/* Catch invite with or without trailing slash and with explicit ID in URL */}
      <Route path="/invite" element={<GroupInvitePage />} />
      <Route path="/invite/" element={<GroupInvitePage />} />
      
      {/* Legacy route redirects */}
      <Route path="/group/invite/:groupId" element={<Navigate to={`/invite/:groupId`} replace />} />
      <Route path="/group/invite/:groupId/" element={<Navigate to={`/invite/:groupId`} replace />} />
      
      {/* Catch all other routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
